'use client'

import { useCallback, useMemo, useState } from 'react'
import Link from 'next/link'
import { IconArrowRight, IconChevronDown, IconSearch } from '@tabler/icons-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { branding } from '@/lib/branding.config'
import { CoordinateTag } from '@/components/guides/meridian'

export interface FaqCategory {
  name: string
  faqs: { question: string; answer: string }[]
}

interface FaqPageClientProps {
  categories: FaqCategory[]
}

/** Category chip: neutral by default, a quiet fill when selected — no accent spend here, that's reserved for the primary CTA below. */
const categoryClass = (active: boolean) =>
  cn(
    'whitespace-nowrap rounded-xl px-3 py-2 text-sm transition-colors border lg:w-full lg:text-left',
    active
      ? 'bg-muted text-foreground border-foreground/20 font-medium'
      : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted'
  )

/**
 * The interactive part of /faq: search, category filter and the accordions.
 * The page itself is a server component so it can export metadata; the
 * questions are passed in from there. This is a filtered-search surface, so
 * it takes the task-page shape — a single coordinate tag, not a meridian
 * rail running through the filters.
 */
export function FaqPageClient({ categories }: FaqPageClientProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({})
  const [selectedCategoryName, setSelectedCategoryName] = useState<string | null>(null)

  const toggleItem = (key: string) => {
    setOpenItems(prev => ({ ...prev, [key]: !prev[key] }))
  }

  const categoryIdForName = useCallback((name: string) => {
    const slug = name
      .toLowerCase()
      .trim()
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
    return `faq-${slug || 'category'}`
  }, [])

  const matches = useCallback(
    (faq: FaqCategory['faqs'][number], query: string) =>
      faq.question.toLowerCase().includes(query.toLowerCase()) ||
      faq.answer.toLowerCase().includes(query.toLowerCase()),
    []
  )

  const filteredCategories = useMemo(
    () =>
      categories
        .map(category => ({ ...category, faqs: category.faqs.filter(faq => matches(faq, searchQuery)) }))
        .filter(category => category.faqs.length > 0),
    [categories, matches, searchQuery]
  )

  const displayedCategories = useMemo(() => {
    if (!selectedCategoryName) return filteredCategories
    const match = filteredCategories.find(c => c.name === selectedCategoryName)
    return match ? [match] : filteredCategories
  }, [filteredCategories, selectedCategoryName])

  const navCategories = useMemo(
    () =>
      filteredCategories.map(category => ({
        name: category.name,
        id: categoryIdForName(category.name),
      })),
    [filteredCategories, categoryIdForName]
  )

  const selectCategory = (name: string | null) => {
    setSelectedCategoryName(name)
    document.getElementById('faq-questions')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Hero: content-sized intro + search, not a full-viewport horizon */}
      <section className="relative overflow-hidden border-b border-border">
        <div aria-hidden className="starfield absolute inset-0 opacity-30" />
        <div className="relative mx-auto max-w-[1280px] px-4 py-16 sm:px-8 md:py-24 lg:px-12">
          <div className="grid items-center gap-10 md:grid-cols-12">
            <div className="md:col-span-7">
              <CoordinateTag>Help center</CoordinateTag>
              <h1 className="mt-5 text-4xl font-bold tracking-tight text-foreground md:text-5xl">
                Frequently Asked Questions
              </h1>
              <p className="mt-4 max-w-2xl text-lg text-muted-foreground md:text-xl">
                Answers about {branding.name} GPU machines: pricing, billing, regions, connecting and more.
              </p>

              <div className="mt-6 flex flex-wrap items-center gap-3 text-sm">
                <Button asChild variant="outline" size="sm" className="rounded-xl">
                  <Link href="/help">
                    Contact support
                    <IconArrowRight className="size-4" />
                  </Link>
                </Button>
                <span className="text-muted-foreground">or browse the categories below</span>
              </div>
            </div>

            {/* Search card */}
            <div className="md:col-span-5">
              <div className="rounded-2xl border border-border bg-card p-5 md:p-6">
                <div className="flex items-center justify-between gap-4">
                  <label htmlFor="faq-search" className="text-sm font-medium text-foreground">
                    Search the FAQ
                  </label>
                  <p className="text-xs text-muted-foreground">Type to filter</p>
                </div>

                <div className="relative mt-3">
                  <IconSearch className="absolute left-4 top-1/2 size-5 -translate-y-1/2 text-muted-foreground" aria-hidden />
                  <input
                    id="faq-search"
                    type="text"
                    placeholder="Search for answers..."
                    value={searchQuery}
                    onChange={(e) => {
                      const nextQuery = e.target.value
                      setSearchQuery(nextQuery)

                      if (!selectedCategoryName) return
                      const hasMatchesInSelected = categories
                        .find(c => c.name === selectedCategoryName)
                        ?.faqs.some(faq => matches(faq, nextQuery))
                      if (!hasMatchesInSelected) setSelectedCategoryName(null)
                    }}
                    className="h-12 w-full rounded-xl border border-border bg-background pl-12 pr-4 text-foreground outline-none transition-all placeholder:text-muted-foreground focus:border-primary/60 focus:ring-2 focus:ring-primary/20"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section — task-shaped: one coordinate tag, no rail through the filters */}
      <section className="py-16">
        <div className="mx-auto max-w-[1280px] px-4 sm:px-8 lg:px-12">
          <CoordinateTag className="mb-8">Filtered view · {filteredCategories.length} categories</CoordinateTag>

          {filteredCategories.length === 0 ? (
            <div className="py-12 text-center">
              <p className="mb-4 text-foreground">
                No results found for <span className="font-semibold">&quot;{searchQuery}&quot;</span>
              </p>
              <Button variant="outline" onClick={() => setSearchQuery('')}>
                Clear Search
              </Button>
            </div>
          ) : (
            <div className="grid gap-10 lg:grid-cols-[240px_1fr]">
              {/* Category menu */}
              <aside className="min-w-0 lg:sticky lg:top-24 lg:self-start">
                <div className="rounded-2xl border border-border bg-card p-4">
                  <p className="mb-3 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Categories
                  </p>
                  <nav className="flex gap-2 overflow-x-auto pb-1 lg:flex-col lg:overflow-visible lg:pb-0">
                    <button type="button" onClick={() => selectCategory(null)} className={categoryClass(selectedCategoryName === null)}>
                      All
                    </button>
                    {navCategories.map(category => (
                      <button
                        type="button"
                        key={category.id}
                        className={categoryClass(selectedCategoryName === category.name)}
                        onClick={() => selectCategory(category.name)}
                      >
                        {category.name}
                      </button>
                    ))}
                  </nav>
                </div>
              </aside>

              {/* Categories + questions */}
              <div id="faq-questions" className="min-w-0 space-y-10 scroll-mt-24">
                {selectedCategoryName ? (
                  <div className="flex items-center justify-between gap-4">
                    <p className="text-sm text-muted-foreground">
                      Showing: <span className="text-foreground">{selectedCategoryName}</span>
                    </p>
                    <Button variant="outline" size="sm" onClick={() => selectCategory(null)}>
                      Clear filter
                    </Button>
                  </div>
                ) : null}

                {displayedCategories.map((category) => {
                  const categoryId = categoryIdForName(category.name)

                  return (
                    <div key={category.name} id={categoryId} className="scroll-mt-24">
                      <h2 className="mb-4 text-xl font-bold text-foreground">{category.name}</h2>
                      <div className="space-y-3">
                        {category.faqs.map((faq, index) => {
                          const key = `${category.name}-${index}`
                          const isOpen = openItems[key]
                          const panelId = `${categoryId}-${index}`

                          return (
                            <div
                              key={key}
                              className={cn(
                                'overflow-hidden rounded-xl border bg-card transition-colors',
                                isOpen ? 'border-foreground/20' : 'border-border hover:border-foreground/15'
                              )}
                            >
                              <button
                                type="button"
                                onClick={() => toggleItem(key)}
                                aria-expanded={isOpen}
                                aria-controls={panelId}
                                className="flex w-full items-center justify-between p-5 text-left"
                              >
                                <span className="pr-4 font-medium text-foreground">{faq.question}</span>
                                <IconChevronDown
                                  className={cn('size-5 shrink-0 transition-transform duration-200', isOpen ? 'rotate-180 text-foreground' : 'text-muted-foreground')}
                                  aria-hidden
                                />
                              </button>
                              <div id={panelId} className={cn('overflow-hidden transition-all duration-200', isOpen ? 'max-h-[800px]' : 'max-h-0')}>
                                <p className="px-5 pb-5 leading-relaxed text-muted-foreground">{faq.answer}</p>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Still Have Questions — arrival */}
      <section className="relative border-t border-border py-16 md:py-24">
        <div className="relative mx-auto max-w-5xl px-4">
          <div className="relative overflow-hidden rounded-3xl border border-border bg-card px-6 py-12 text-center md:px-10 md:py-16">
            <div aria-hidden className="bg-hero-mesh absolute inset-0" />
            <div className="relative">
              <CoordinateTag className="justify-center">Transit complete</CoordinateTag>
              <h2 className="mt-4 text-3xl font-bold tracking-tight text-foreground md:text-5xl">
                Still Have Questions?
              </h2>
              <p className="mx-auto mt-4 max-w-2xl text-lg text-muted-foreground md:text-xl">
                Can&apos;t find what you&apos;re looking for? Our support team is here to help.
              </p>
              <Button asChild variant="primary" size="lg" className="mt-10 rounded-full px-10 glow-accent">
                <Link href="/help">
                  Contact Support
                  <IconArrowRight className="size-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
